import { useCallback, useMemo, useState } from 'react';

import { useAtomValue } from 'jotai';
import { AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router';

import { ClubTable } from '@blocks';
import { isFiniteNumber } from '@core';
import { useSyncState } from '@hooks';
import { useClubCollections, useActivity, useUserClubs, useActivitySwings } from '@queries';
import { activityIdAtom } from '@store';

import { ClubSaveModal, SaveMode, SAVE_MODE } from './ClubSaveModal';
import * as css from './ClubSelection.css';
import { ClubSelectionControls } from './ClubSelectionControls';
import { useCreateActivityFlow } from './useCreateActivityFlow';
import { useDisplayMode } from './useDisplayMode';
import { useUpdateActivity } from './useUpdateActivity';

const PUTTER = 'Putter';

type ClubSelectionProps = {
    activeUserId?: string;
    isCurrentActivity?: boolean;
    getActivityLink?: (activityId: number) => string;
    onClose: () => void;
};

export function ClubSelection({ activeUserId, isCurrentActivity, getActivityLink, onClose }: ClubSelectionProps) {
    const { data: clubsCollections = [], isLoading: isCollectionsLoading } = useClubCollections();
    const navigate = useNavigate();

    const activityId = useAtomValue(activityIdAtom);
    const { data: activity, isLoading: isActivityLoading } = useActivity(activityId);
    const { data: userClubs } = useUserClubs(activeUserId);
    const { displayMode } = useDisplayMode();
    const { data: swings, isLoading: isLoadingSwings } = useActivitySwings(activityId);

    const bagCollections = useMemo(() => {
        const userClubIds = userClubs?.map((club) => club.id);
        return clubsCollections.map(({ typeName, id, clubs }) => ({
            typeName,
            id,
            clubs: clubs.filter((c) => userClubIds?.includes(c.id)),
        }));
    }, [clubsCollections, userClubs]);

    const putterId = useMemo(
        () => clubsCollections.find(({ typeName }) => typeName === PUTTER)?.clubs?.[0]?.id,
        [clubsCollections],
    );
    const isLoading = isCollectionsLoading || isLoadingSwings;
    const [selectedClubId, setSelectedClubId] = useSyncState<number>(
        activity?.club?.id || clubsCollections?.[0]?.clubs?.[0]?.id,
        isCollectionsLoading || isActivityLoading,
    );
    const { handleCreateWithClub, isPending: isCreatePending } = useCreateActivityFlow({
        selectedClub: selectedClubId,
    });
    const { handleUpdateCurrentActivity, isPending: isUpdatePending } = useUpdateActivity({
        selectedClub: selectedClubId,
        activityId,
    });
    const isMutating = isCreatePending || isUpdatePending;

    const [saveMode, setSaveMode] = useState<SaveMode>(null);
    const handleDismiss = useCallback(() => setSaveMode(null), []);

    const handleSave = useCallback(async () => {
        if (!isFiniteNumber(selectedClubId)) {
            return;
        }

        const hasNoSwings = !(swings?.length ?? 0 > 0);
        const isSelectingPutter = selectedClubId === putterId;
        const isEmptyCurrentActivity = isCurrentActivity && hasNoSwings;
        const shouldUpdateCurrent = isEmptyCurrentActivity && !isSelectingPutter;
        const shouldCreatePutterActivity = isEmptyCurrentActivity && isSelectingPutter && getActivityLink;
        // If the user has an empty activity and he is selecting a new club that is not a putter
        // update the current one and close the screen
        if (shouldUpdateCurrent) {
            await handleUpdateCurrentActivity();
            onClose();

            return;
        }

        // If the user wants to switch to a putter in kiosk mode with no swings,
        // we create a fresh activity straight away.
        if (shouldCreatePutterActivity) {
            const newActivityId = await handleCreateWithClub();

            if (isFiniteNumber(newActivityId)) {
                navigate(getActivityLink(newActivityId));
            }

            return;
        }

        // Decide the modal variant once before opening it.
        // Do not derive this inside ClubSaveModal from live route/current-activity state,
        // because that state can change during create+navigate and cause the modal body to flicker.
        if (isCurrentActivity && isSelectingPutter) {
            setSaveMode(SAVE_MODE.PUTTER);
            return;
        }

        setSaveMode(getActivityLink && isCurrentActivity ? SAVE_MODE.CREATE : SAVE_MODE.UPDATE);
    }, [
        getActivityLink,
        handleCreateWithClub,
        handleUpdateCurrentActivity,
        isCurrentActivity,
        navigate,
        onClose,
        putterId,
        selectedClubId,
        swings,
    ]);

    const handleCreate = useCallback(async () => {
        if (typeof getActivityLink === 'undefined') {
            return;
        }

        const newActivityId = await handleCreateWithClub();

        if (isFiniteNumber(newActivityId)) {
            setSaveMode(null);
            navigate(getActivityLink(newActivityId));
        }
    }, [getActivityLink, handleCreateWithClub, navigate]);

    const handleUpdate = useCallback(async () => {
        await handleUpdateCurrentActivity();
        setSaveMode(null);
        onClose();
    }, [handleUpdateCurrentActivity, onClose]);

    return (
        <>
            <div className={css.root}>
                <ClubSelectionControls isLoading={isLoading || isMutating} onSave={handleSave} onCancel={onClose} />
                <div className={css.contentWrapper}>
                    {clubsCollections.length > 0 && (
                        <AnimatePresence mode="wait">
                            <ClubTable
                                key={displayMode}
                                clubs={displayMode === 'bag' ? bagCollections : clubsCollections}
                                selectedClubs={selectedClubId ? [selectedClubId] : []}
                                onClubClick={setSelectedClubId}
                            />
                        </AnimatePresence>
                    )}
                </div>
            </div>
            {saveMode && (
                <ClubSaveModal
                    saveMode={saveMode}
                    isCreatePending={isCreatePending}
                    isUpdatePending={isUpdatePending}
                    onCreate={handleCreate}
                    onDismiss={handleDismiss}
                    onUpdate={handleUpdate}
                />
            )}
        </>
    );
}
